import { castToFloatLocalized } from '@app/utils/string-helpers'
import { SfLog } from '@app/adapters/logger'
import {
  showValidationError,
  hideValidationError,
  parseDateValue,
} from './utils'
import { DateRangeRules, NumericRules } from './types'

export const validateInput = (input: HTMLInputElement): void => {
  if (!input) {
    return
  }

  const rulesString = input.getAttribute('data-live-validation-rules')
  if (!rulesString) {
    return
  }

  if (input.value === '' || input.value === null) {
    const valueRequired = input.getAttribute('data-required') === 'true'
    hideValidationError(input)
    if (valueRequired) {
      input.classList.add('sf-required')
    }
    return
  }

  switch (input.getAttribute('data-value-type')) {
    case 'integer':
    case 'currency':
    case 'currency_dc': {
      let rules: NumericRules
      try {
        rules = JSON.parse(rulesString) as NumericRules
      } catch (e) {
        SfLog.error('Error parsing numeric live validation rules', { error: e })
        return
      }
      const precision = parseInt(input.getAttribute('data-precision') || '2')
      const value = parseFloat(
        castToFloatLocalized(input.value).toFixed(precision)
      )

      if (isNaN(value)) {
        showValidationError(
          input,
          window.I18n.t('liquid.input_validation.errors.numeric.invalid')
        )
        return
      }

      const { valid, errorMessage } = validateNumericValue(
        value,
        precision,
        rules
      )

      if (valid) {
        hideValidationError(input)
        return
      }
      showValidationError(input, errorMessage || '')
      return
    }
    case 'date': {
      let rules: DateRangeRules
      try {
        rules = JSON.parse(rulesString) as DateRangeRules
      } catch (e) {
        SfLog.error('Error parsing date live validation rules', { error: e })
        return
      }

      const date = input.value
      const targetFormat =
        input.getAttribute('data-moment-js-format') ||
        (input.getAttribute('data-date-format') || '').toUpperCase()
      const parsedDateValue = parseDateValue(date, targetFormat)

      if (!parsedDateValue) {
        showValidationError(
          input,
          window.I18n.t('liquid.input_validation.errors.date_range.invalid')
        )
        return
      }

      const { valid, errorMessage } = validateDateValue(
        parsedDateValue,
        targetFormat,
        rules
      )

      if (valid) {
        hideValidationError(input)
        return
      }
      showValidationError(input, errorMessage || '')
      return
    }
    default:
      return
  }
}

const validateDateValue = (
  date: Date,
  format: string,
  rules: DateRangeRules
): {
  valid: boolean
  errorMessage?: string
} => {
  const startDate = rules.start_date
    ? parseDateValue(rules.start_date, format)
    : undefined
  const endDate = rules.end_date
    ? parseDateValue(rules.end_date, format)
    : undefined

  if (startDate && endDate) {
    if (date < startDate || date > endDate) {
      return {
        valid: false,
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.date_range.between',
          { start_date: rules.start_date, end_date: rules.end_date }
        ),
      }
    }
  } else {
    if (startDate && date < startDate) {
      return {
        valid: false,
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.date_range.start_date',
          { start_date: rules.start_date }
        ),
      }
    }

    if (endDate && date > endDate) {
      return {
        valid: false,
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.date_range.end_date',
          { end_date: rules.end_date }
        ),
      }
    }
  }

  return { valid: true }
}

const validateNumericValue = (
  value: number,
  precision: number,
  rules: NumericRules
): {
  valid: boolean
  errorMessage?: string
} => {
  const minValue =
    rules.min !== undefined
      ? parseFloat(castToFloatLocalized(rules.min).toFixed(precision))
      : undefined
  const maxValue =
    rules.max !== undefined
      ? parseFloat(castToFloatLocalized(rules.max).toFixed(precision))
      : undefined

  if (minValue !== undefined && maxValue !== undefined) {
    if (value < minValue || value > maxValue) {
      return {
        valid: false,
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.numeric.between',
          { min: minValue, max: maxValue }
        ),
      }
    }
  } else {
    if (minValue !== undefined && value < minValue) {
      return {
        valid: false,
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.numeric.min',
          {
            min: minValue,
          }
        ),
      }
    }

    if (maxValue !== undefined && value > maxValue) {
      return {
        valid: false,
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.numeric.max',
          {
            max: maxValue,
          }
        ),
      }
    }
  }

  return { valid: true }
}
